/* tslint:disable:max-line-length */
import {Inject, Injectable, NgZone, OnDestroy} from '@angular/core';
import {MultiActivity} from '@docma-mm-core/activity/multi-activity';
import {AttachmentFileDbEntity, mapAttachmentSourceToEntity} from '@docma-mm-core/attachment/attachment-db.entity';
import {Group} from '@docma-mm-core/group/group';
import {ChangeState} from '@docma-mm-core/model/change-state/change-state';
import {ChangeStatusEnum} from '@docma-mm-core/model/change-state/change-status.enum';
import {LoadState} from '@docma-mm-core/model/load-state/load-state';
import {SyncState} from '@docma-mm-core/model/sync-state/sync-state';
import {Guid} from 'guid-typescript';
import {MmDbService} from '../../mm-db/mm-db-service';
import {MmProjectDbCollections} from '../../mm-db/mm-project-db-schema';
import {Fault, ViewFault} from './fault';
import {FaultData} from './fault-data';
import {AttachmentDbEntity, FaultDbEntity, mapEntityToFault, mapFaultToEntity, mapGroupToEntity} from './fault-db.entity';
import {FaultPatchService} from './patch/fault-patch-service';
import {FaultsEdit} from '@docma-mm-core/fault/faults-edit';
import {LoadStatusEnum} from '@docma-mm-core/model/load-state/load-state.enum';
import {Activity} from '@docma-mm-core/activity/activity';
import {ActivityEdit} from '@docma-mm-core/activity/activity-edit';
import {ActivityPatchModeEnum} from '@docma-mm-core/activity/activity-patch-mode.enum';
import {SyncStatusEnum} from '@docma-mm-core/model/sync-state/sync-status.enum';
import { IOfflineService } from '../../mm-offline/interfaces/offline-service.interface';
import { OFFLINE_SERVICE } from '../../mm-offline/interfaces/offline-service.token';
import { PlatformModeStateService } from '../../mm-offline/services/platform-mode-state.service';
import { FaultCount } from './fault-counts';

@Injectable()
export class FaultPersistenceService implements OnDestroy {

  constructor(private readonly mmDbService: MmDbService,
              private readonly patchService: FaultPatchService,
              private readonly  faultPatchService: FaultPatchService,
              private readonly platformModeStateService: PlatformModeStateService,
              @Inject(OFFLINE_SERVICE) private offlineService: IOfflineService,
              private ngZone: NgZone) {
  }

  ngOnDestroy(): void {
  }

  async saveChangeState(mydocmaUid: string, faultUid: string, changeState: ChangeState<FaultData>): Promise<ChangeState<FaultData>> {
    return await this.ngZone.runOutsideAngular(async () => {
      const db = await this.mmDbService.getProjectDb(mydocmaUid);
      const entity = await db.get(MmProjectDbCollections.faults as never, faultUid);
      if (entity) {
        entity.changeState = changeState;
        await db.set(MmProjectDbCollections.faults as never, entity);
      }
      return new ChangeState<FaultData>(changeState);
    });
  }

  async saveChangeStates(mydocmaUid: string, faultUids: string[],
                         changeState: ChangeState<FaultData>,
                         havingChangeStates?: ChangeStatusEnum[]): Promise<{ faultUid: string, changeState: ChangeState<FaultData>}[]> {
    if (!faultUids || faultUids.length === 0) {
      return [];
    }

    return await this.ngZone.runOutsideAngular(async () => {
      const db = await this.mmDbService.getProjectDb(mydocmaUid);
      const entities = await db.getAll(MmProjectDbCollections.faults as never, IDBKeyRange.bind(faultUids));
      if (!entities) {
        throw new Error(`An error while persisting fault change states.`);
      }

      const updatedEntities: any[] = [];
      const savedChangeStates: { faultUid: string, changeState: ChangeState<FaultData> }[] = [];

      for (const faultUid of faultUids) {
        const entity = entities.find(x => x.uid === faultUid);
        if (!havingChangeStates || havingChangeStates.findIndex(e => e === entity.changeState.status) !== -1) {
          entity.changeState = changeState;
          updatedEntities.push(entity);
          savedChangeStates.push({faultUid, changeState});
        }
      }

      await db.putAll(MmProjectDbCollections.faults as never, updatedEntities);
      return savedChangeStates;
    });
  }

  async getChangeState(mydocmaUid: string, faultUid: string): Promise<ChangeState<FaultData>> {
    const db = await this.mmDbService.getProjectDb(mydocmaUid);
    const entity = await db.get(MmProjectDbCollections.faults as never, faultUid);
    if (!entity) {
      return new ChangeState<FaultData>({status: ChangeStatusEnum.NotChanged});
    }
    return new ChangeState<FaultData>(entity.changeState );
  }

  async getChangeStates(mydocmaUid: string, faultUids: string[]): Promise<{ faultUid: string, changeState: ChangeState<FaultData>}[]> {
    if (!faultUids || faultUids.length === 0) {
      return [];
    }

    const db = await this.mmDbService.getProjectDb(mydocmaUid);
    const entities = await db.getAll(MmProjectDbCollections.faults as never, IDBKeyRange.bind(faultUids));
    if (!entities) {
      return null;
    }
    return entities.map(entity => ({ faultUid: entity.uid, changeState: new ChangeState<FaultData>(entity.changeState)}));
  }

  async saveLoadState(mydocmaUid: string, faultUid: string, state: LoadState<FaultData>) {
    return await this.ngZone.runOutsideAngular(async () => {
      const db = await this.mmDbService.getProjectDb(mydocmaUid);
      const entity = await db.get(MmProjectDbCollections.faults as never, faultUid);
      if (entity) {
        entity.loadState = state;
        entity.id = state.data?.Id ?? entity.id;
        await db.set(MmProjectDbCollections.faults as never, entity);
      }
    });
  }

  async getLoadState(mydocmaUid: string, faultUid: string): Promise<LoadState<FaultData>> {
    const db = await this.mmDbService.getProjectDb(mydocmaUid);
    const entity = await db.get(MmProjectDbCollections.faults as never, faultUid);
    if (entity) {
      return entity.loadState ;
    }
    return  null;
  }

  async getLoadStates(mydocmaUid: string, faultUids?: string[]): Promise<LoadState<FaultData>[]> {
    const db = await this.mmDbService.getProjectDb(mydocmaUid);
    let entities: any[];
    if (faultUids && faultUids.length > 0) {
      entities = await db.getAll(MmProjectDbCollections.faults as never, IDBKeyRange.bind(faultUids));
    }
    else {
      entities = await db.getAll(MmProjectDbCollections.faults as never);
    }

    if (entities && entities.length > 0) {
      return Promise.all(entities.map(async e => e.loadState));
    }
    return [];
  }



  async getSyncState(mydocmaUid: string, faultUid: string): Promise<SyncState> {
    const db = await this.mmDbService.getProjectDb(mydocmaUid);
    const entity = await db.get(MmProjectDbCollections.faults as never, faultUid);
    if (entity) {
      return entity.syncState;
    }
    return  null;
  }

  async saveSyncState(mydocmaUid: string, uid: string, state: SyncState): Promise<SyncState> {
    return await this.ngZone.runOutsideAngular(async () => {
      const db = await this.mmDbService.getProjectDb(mydocmaUid);
      const entity = await db.get(MmProjectDbCollections.faults as never, uid);
      if (entity) {
        entity.syncState = {...state};
        delete entity.syncState.changes;
        await db.set(MmProjectDbCollections.faults as never, entity);
      }
      return state;
    });
  }

  async saveLoadStates(mydocmaUid: string, uids: string[], states: LoadState<FaultData>[]) {
    return await this.ngZone.runOutsideAngular(async () => {
      const db = await this.mmDbService.getProjectDb(mydocmaUid);
      const entities = await db.getAll(MmProjectDbCollections.faults as never, IDBKeyRange.bind(uids));
      if (entities) {
        for (const entity of entities) {
          const state = states.find(s => s.data.UniqueId === entity.uid);
          if (state) {
            entity.loadState = {...state};
          }
        }
        await db.putAll(MmProjectDbCollections.faults as never, entities);
      }
    });
  }

  async saveSyncStates(mydocmaUid: string, uids: string[], state: SyncState) {
    return await this.ngZone.runOutsideAngular(async () => {
      const db = await this.mmDbService.getProjectDb(mydocmaUid);
      const entities = await db.getAll(MmProjectDbCollections.faults as never, IDBKeyRange.bind(uids));
      if (entities) {
        for (const entity of entities) {
          entity.syncState = {...state};
          delete entity.syncState.changes;
        }
        await db.putAll(MmProjectDbCollections.faults as never, entities);
      }
    });
  }

  async clearAllSyncStates(mydocmaUid: string, uids: string[]) {
    return await this.ngZone.runOutsideAngular(async () => {
      const db = await this.mmDbService.getProjectDb(mydocmaUid);
      const entities = await db.getAll(MmProjectDbCollections.faults as never, IDBKeyRange.bind(uids));
      if (entities) {
        for (const entity of entities) {
          entity.syncState = {...new SyncState({status: SyncStatusEnum.NotSync})};
          delete entity.syncState.changes;

          if (entity.attachments) {
            for (const attachmentEntity of entity.attachments) {
              attachmentEntity.syncState = {...new SyncState({status: SyncStatusEnum.NotSync})};
              delete attachmentEntity.syncState.changes;
            }
          }

          if (entity.activities) {
            for (const activityEntity of entity.activities) {
              activityEntity.syncState = {...new SyncState({status: SyncStatusEnum.NotSync})};
              delete activityEntity.syncState.changes;
            }
          }
        }
        await db.putAll(MmProjectDbCollections.faults as never, entities);
      }
    });
  }

  async getAll(mydocmaUid: string, faultUids?: string[]): Promise<Fault[]> {
    const db = await this.mmDbService.getProjectDb(mydocmaUid);
    let entities: any[];
    if (faultUids && faultUids.length > 0) {
      entities = await db.getAll(MmProjectDbCollections.faults as never, IDBKeyRange.bind(faultUids));
    }
    else {
      entities = await db.getAll(MmProjectDbCollections.faults as never);
    }

    if (entities && entities.length > 0) {
      return Promise.all(entities.map(async e => await mapEntityToFault(e)));
    }
    return [];
  }

  async get(mydocmaUid: string, faultUid: string): Promise<Fault> {
    const db = await this.mmDbService.getProjectDb(mydocmaUid);
    const entity = await db.get(MmProjectDbCollections.faults as never, faultUid);
    if (entity) {
      return await mapEntityToFault(entity);
    }
    return null;
  }

  async removeAll(mydocmaUid: string, faultUids: string[], groupsUids: string[], attachmentsUids: string[]): Promise<any> {
    return await this.ngZone.runOutsideAngular(async () => {
      const dbService = await this.mmDbService.getProjectDb(mydocmaUid);
      const db = await dbService.db;

      const filesOfFault = await db.getAllKeysFromIndex(MmProjectDbCollections.files as never, 'faultUid', faultUids);
      const fileKeys = [...(attachmentsUids ?? []), ...filesOfFault];
      // const allGroups = (groupsUids ? await db.getAll(MmProjectDbCollections.groups as never, groupsUids) : []);
      // const allFiles = await db.getAll(MmProjectDbCollections.files as never);

      // const groupKeys = allGroups.map(x => x.uid);
      // const fileKeys = allFiles.filter(f => faultUids.includes(f.faultUid) || attachmentsUids.includes(f.uid)).map(x => x.uid);

      // for (const faultUid of (faultUids || [])) {
      //   fileKeys.push(... );
      // }

      const transaction = db.transaction([MmProjectDbCollections.faults as never, MmProjectDbCollections.groups as never, MmProjectDbCollections.files as never], 'readwrite');
      try {
        const faultStore = transaction.objectStore(MmProjectDbCollections.faults as never);
        const fileStore = transaction.objectStore(MmProjectDbCollections.files as never);
        const groupStore = transaction.objectStore(MmProjectDbCollections.groups as never);
        await Promise.all(fileKeys.map(async k => fileStore.delete(k)));
        await Promise.all((groupsUids ?? []).map(async g => groupStore.delete(g)));
        await Promise.all(faultUids.map(async f => faultStore.delete(f)));
        await transaction.done;
      } catch (err) {
        transaction.abort();
        throw err;
      }

      if (this.platformModeStateService.isLocalModeEnabled) {
        await Promise.all(fileKeys.map(async fi => {
          await this.offlineService.removeAttachment(fi as string);
        }));
        await this.patchService.removeByFaultUids(mydocmaUid, faultUids);
      }

      return;
    });
  }

  async removeFile(mydocmaUid: string, fileUid: string): Promise<any> {
    return await this.ngZone.runOutsideAngular(async () => {
      const dbService = await this.mmDbService.getProjectDb(mydocmaUid);
      const db = await dbService.db;
      const transaction = db.transaction([MmProjectDbCollections.files as never], 'readwrite');
      const fileStore = transaction.objectStore(MmProjectDbCollections.files as never);
      try {
        await fileStore.delete(fileUid);
        await transaction.done;
      } catch (err) {
        transaction.abort();
        throw err;
      }
      return;
    });
  }

  async saveFaultsEdit(mydocmaUid: string, faultsEdit: FaultsEdit): Promise<void> {
    await this.ngZone.runOutsideAngular(async () => {
      const dbService = await this.mmDbService.getProjectDb(mydocmaUid);
      const db = await dbService.db;
      const transaction = db.transaction([MmProjectDbCollections.faults as never], 'readwrite');
      const faultStore = transaction.objectStore(MmProjectDbCollections.faults as never);

      try {
        // Parallele Zuordnung aller Entities
        const faultEntities = await Promise.all(
          Array.from(faultsEdit.faults.values()).map(fault => mapFaultToEntity(fault))
        );

        // Batch-Put aller Entities
        await Promise.all(
          faultEntities.map(entity => faultStore.put(entity))
        );

        await transaction.commit();
        console.warn('commit');
      } catch (err) {
        transaction.abort();
      }
    });
  }

  async saveActivityEdit(mydocmaUid: string, activityEdit: ActivityEdit): Promise<Fault[]> {
    const faultDbEntities: { [id: number]: { faultEntity: FaultDbEntity, fault: Fault, setUid: boolean } } = {};
    const savedFaults: Fault[] = [];

    await Promise.all(Object.keys(activityEdit.faults).map(async faultId => {
      const fault = activityEdit.faults[faultId];
      const faultEntity = await mapFaultToEntity(fault);
      faultDbEntities[faultEntity.id] = { faultEntity, fault, setUid: false };
      savedFaults.push(fault);
    }));

    return await this.ngZone.runOutsideAngular(async () => {
      const dbService = await this.mmDbService.getProjectDb(mydocmaUid);
      const db = await dbService.db;
      const transaction = db.transaction([MmProjectDbCollections.faults as never, MmProjectDbCollections.files as never], 'readwrite');

      const faultStore = transaction.objectStore(MmProjectDbCollections.faults as never);
      try {
        await Promise.all(Object.entries(faultDbEntities).map(async ([fKey, fValue]) => {
          await faultStore.put(fValue.faultEntity);
        }));
        await transaction.commit();
      } catch (err) {
        transaction.abort();
        throw err;
      }
      return savedFaults;
    });
  }

  async saveMultiActivity(mydocmaUid: string, multiActivity: MultiActivity): Promise<Fault[]> {
    const faultDbEntities: { [id: number]: { faultEntity: FaultDbEntity, fileEntities: AttachmentFileDbEntity[], fault: Fault, setUid: boolean } } = {};
    const savedFaults: Fault[] = [];

    const entities = (multiActivity.patchMode === ActivityPatchModeEnum.MultiActivity_Single ? multiActivity.entities.filter(e => e.IsInitActivity) : multiActivity.entities);
    // const groupEntities = (multiActivity.patchMode === ActivityPatchModeEnum.MultiActivity_Single ? multiActivity.entities.filter(e => !e.IsInitActivity) : []);

    for (const entity of entities) {
      const faultEntity = await mapFaultToEntity(entity.Fault);
      const fileEntities = await this.getFaultFilesEntities(entity.Fault, multiActivity);
      let setUidInternal = false;
      if (!faultEntity.uid && entity.Fault.changeState.status === ChangeStatusEnum.New) {
        faultEntity.uid = Guid.create().toString();
        faultEntity.changeState.data.UniqueId = faultEntity.uid;
        setUidInternal = true;
        fileEntities.forEach(f => f.faultUid = faultEntity.uid);
      }
      faultDbEntities[faultEntity.id] = { faultEntity, fileEntities, fault: entity.Fault, setUid: setUidInternal };
      savedFaults.push(entity.Fault);
    }

    await this.ngZone.runOutsideAngular(async () => {
      const dbService = await this.mmDbService.getProjectDb(mydocmaUid);
      const db = await dbService.db;
      const transaction = db.transaction([MmProjectDbCollections.faults as never, MmProjectDbCollections.files as never], 'readwrite');

      const faultStore = transaction.objectStore(MmProjectDbCollections.faults as never);
      const fileStore = transaction.objectStore(MmProjectDbCollections.files as never);
      try {
        await Promise.all(Object.entries(faultDbEntities).map(async ([fKey, fValue]) => {
          await faultStore.put(fValue.faultEntity);
          for (const fe of fValue.fileEntities) {
            await fileStore.put(fe);
          }
        }));
        await transaction.commit();
      } catch (err) {
        transaction.abort();
        throw err;
      }
    });

    for (const fKey in faultDbEntities) {
      if (faultDbEntities[fKey].setUid) {
        faultDbEntities[fKey].fault.uid = faultDbEntities[fKey].fault.uid;
        faultDbEntities[fKey].fault.changeState.data.UniqueId = faultDbEntities[fKey].fault.uid;
      }
    }
    return savedFaults;
  }

  async save(mydocmaUid: string, fault: Fault, attachmentAlreadyDownloaded: boolean = false): Promise<Fault> {
    const dbService = await this.mmDbService.getProjectDb(mydocmaUid);
    const entity = await mapFaultToEntity(fault);
    const fileEntities = await this.getFaultFilesEntities(fault, null);
    const setUid = false;
    if (!entity.uid && fault.changeState.status === ChangeStatusEnum.New) {
      throw new Error(`Couldn't persist new fault to index database. (no uid set)`);
      // entity.uid = Guid.create().toString();
      // entity.changeState.data.UniqueId = entity.uid;
      // setUid = true;
      // fileEntities.forEach(f => f.faultUid = entity.uid);
    }

    if (this.platformModeStateService.isNative && this.platformModeStateService.isLocalModeEnabled) {
      await Promise.all(fileEntities.map(async f => {
        if (f.loadState.data?.buffer) {
          const matchingAttachment = fault.attachments?.find(a => a.uid === f.uid) ?? fault.activities?.flatMap(a => a.attachments ?? []).find(a => a.uid === f.uid);
          const isExplicitlyNotChanged  = matchingAttachment?.changeState?.status === ChangeStatusEnum.NotChanged;
          if (!this.platformModeStateService.useNewAttachmentDownload || (!attachmentAlreadyDownloaded && !isExplicitlyNotChanged)) {
            const blob = new Blob([f.loadState.data.buffer]);
            await this.offlineService.saveAttachment(blob, f.uid);
          }
          f.loadState.data.buffer = null;
        }
      }));
    }


    await this.ngZone.runOutsideAngular(async () => {

      const db = await dbService.db;
      const transaction = db.transaction([MmProjectDbCollections.faults as never, MmProjectDbCollections.files as never], 'readwrite');

      const faultStore = transaction.objectStore(MmProjectDbCollections.faults as never);
      const fileStore = transaction.objectStore(MmProjectDbCollections.files as never);
      try {
        await faultStore.put(entity);
        await Promise.all(fileEntities.map(async f => await fileStore.put(f)));
        await transaction.commit();
      } catch (err) {
        transaction.abort();
        throw err;
      }
    });

    if (setUid) {
      fault.uid = entity.uid;
      fault.changeState.data.UniqueId = entity.uid;
    }

    return fault;
  }


  // async getGroup(mydocmaUid: string, groupUid: string): Promise<Group> {
  //   const dbService = await this.mmDbService.getProjectDb(mydocmaUid);
  //   const db = await dbService.db;
  //   const group = await db.get(MmProjectDbCollections.groups as never, groupUid);
  //
  //   if (!group) {
  //     const transaction = db.transaction([MmProjectDbCollections.groups as never], 'readwrite');
  //     const groupsStore = transaction.objectStore(MmProjectDbCollections.groups as never);
  //
  //     this.groupApiService.createGroup()
  //   }
  //   else {
  //     return group;
  //   }
  // }

  async saveGroup(mydocmaUid: string, group: Group): Promise<Group> {
    const dbService = await this.mmDbService.getProjectDb(mydocmaUid);
    const entity = await mapGroupToEntity(group);

    let setUid = false;
    if (!entity.uid && group.changeState.status === ChangeStatusEnum.New) {
      setUid = true;
    }

    await this.ngZone.runOutsideAngular(async () => {
      const db = await dbService.db;
      const transaction = db.transaction([MmProjectDbCollections.groups as never], 'readwrite');
      const groupsStore = transaction.objectStore(MmProjectDbCollections.groups as never);
      try {
        await groupsStore.put(entity);
        await transaction.commit();
      } catch (err) {
        transaction.abort();
        throw err;
      }
    });

    if (setUid) {
      group.uid = entity.uid;
      group.changeState.data.UniqueId = entity.uid;
    }
    return group;
  }

  async removeGroup(mydocmaUid: string, groupUid: string): Promise<any> {
    const groupPatches = await this.faultPatchService.getByGroupUid(mydocmaUid, groupUid);
    if ((groupPatches?.length ?? 0) > 0) {
      return;
    }

    return await this.ngZone.runOutsideAngular(async () => {
      const dbService = await this.mmDbService.getProjectDb(mydocmaUid);
      const db = await dbService.db;
      const transaction = db.transaction([MmProjectDbCollections.groups as never], 'readwrite');
      try {
        const groupStore = transaction.objectStore(MmProjectDbCollections.groups as never);
        await groupStore.delete(groupUid);
        await transaction.done;
      } catch (err) {
        transaction.abort();
        throw err;
      }
      return;
    });
  }

  private async getFaultFilesEntities(fault: Fault, multiActivity: MultiActivity): Promise<AttachmentFileDbEntity[]> {
    const entities = [];
    if (fault.attachments) {
      for (const a of fault.attachments) {
        if (fault.downloaded || a.changeState.status === ChangeStatusEnum.New) {
          const entity =  await mapAttachmentSourceToEntity(fault.uid, null, a);
          if (entity) {
            entities.push(entity);
           }
         }
      }
    }
    if (fault.activities) {
      for (const ac of fault.activities) {
        for (const a of ac.attachments) {
          const groupUid = multiActivity?.group?.uid;
          const faultUids = multiActivity?.entities?.map(m => m.Fault.uid);

          if (fault.downloaded || a.changeState.status === ChangeStatusEnum.New) {
            const entity =  await mapAttachmentSourceToEntity(fault.uid, groupUid, a);
            if (entity) {
              entities.push(entity);
             }
           }
        }
      }
    }
    return entities;
  }

  async saveOrRemoveIfNotNeeded(mydocmaUid: string, fault: Fault) {
    const patches = await this.patchService.getByFaultUid(mydocmaUid, fault.uid);
    const hasPatches = ((patches?.length ?? 0) > 0);

    if (fault.downloaded || fault.hasAnyChanges()) {
      await this.save(mydocmaUid, fault);
      return;
    } else if (!hasPatches) {
      /* TODO MMW-5194 */
      await this.removeAll(mydocmaUid, [fault.uid], null, null);
    }
  }


  async removeIfNotNeeded(mydocmaUid: string, faultUids: string[], groupUids: string[], attachmentUids: string[]) {
    const db = await this.mmDbService.getProjectDb(mydocmaUid);

    const entities = await db.getAll(MmProjectDbCollections.faults as never, faultUids);
    const groupEntities = await db.getAll(MmProjectDbCollections.groups as never, groupUids);
    const attachmentEntities = await db.getAll(MmProjectDbCollections.files as never, attachmentUids);

    const patches = await this.patchService.getAll(mydocmaUid);
    const patchesByFUid = this.patchService.groupByFuid(patches);
    const patchesByGUid = this.patchService.groupByGuid(patches);
    const patchesByOUid = this.patchService.groupByOuid(patches);

    const faultToDelete = entities.filter(f => !(f.downloaded || this.hasAnyChanges(f)) && !patchesByFUid.hasOwnProperty(f.uid) ).map(f => f.uid);
    //const groupsToDelete = groupEntities.filter(g => !(g.downloaded || this.hasAnyChanges(g)) && !patchesByGUid.hasOwnProperty(g.uid) ).map(g => g.uid);
    //const attachmentsToDelete = attachmentEntities.filter(att => !(att.downloaded || this.hasAttachmentAnyChanges(att)) && !patchesByOUid.hasOwnProperty(att.uid) ).map(att => att.uid);

    await this.removeAll(mydocmaUid, faultToDelete,[],[]);
    return faultToDelete;
  }

  private hasAnyChanges(entity: FaultDbEntity): boolean {
    if (entity.changeState && entity.changeState.status !== ChangeStatusEnum.NotChanged) {
      return true;
    }
    if (entity.attachments) {
      for (const a of entity.attachments) {
        if (a.changeState && a.changeState.status !== ChangeStatusEnum.NotChanged) {
          return true;
        }

        if (a.scribbles) {
          for (const sc of a.scribbles) {
            if (sc.changeState && sc.changeState.status !== ChangeStatusEnum.NotChanged) {
              return true;
            }
          }
        }
      }
    }
    if (entity.activities) {
      for (const a of entity.activities) {
        if (a.changeState && a.changeState.status !== ChangeStatusEnum.NotChanged) {
          return true;
        }
        if (a.attachments) {
          for (const aa of a.attachments) {
            if (aa.changeState && aa.changeState.status !== ChangeStatusEnum.NotChanged) {
              return true;
            }

            if (aa.scribbles) {
              for (const sc of aa.scribbles) {
                if (sc.changeState && sc.changeState.status !== ChangeStatusEnum.NotChanged) {
                  return true;
                }
              }
            }
          }
        }
      }
    }
  }

  private hasAttachmentAnyChanges(entity: AttachmentDbEntity): boolean {
    if (entity.changeState && entity.changeState.status !== ChangeStatusEnum.NotChanged) {
      return true;
    }

    if (entity.scribbles) {
      for (const sc of entity.scribbles) {
        if (sc.changeState && sc.changeState.status !== ChangeStatusEnum.NotChanged) {
          return true;
        }
      }
    }
  }

  async removePersitedActivites(activity: Activity, myDocmaUid: string, groupDelete: boolean = false, modifiedOn: Date) {

    const savedFaults = await this.getAll(myDocmaUid);
    const groupId = activity.data.Group.Id;

    savedFaults.forEach(async fault => {
      let changed = false;
      fault.activities.filter(persistendActivites => (groupDelete && persistendActivites.data?.Group?.Id === groupId) || (!groupDelete && persistendActivites.data?.Id === activity.id))
                       .forEach(activity => {
                            fault.removeActivity(activity);
                            changed = true;
                        });
      if (changed) {
        fault.data.ModifiedOn = modifiedOn;
        fault.loadState.data.ModifiedOn = modifiedOn;
        await this.save(myDocmaUid, fault);
      }
    });
  }

  async updateSyncStatus(fault: ViewFault, allFaults?: Fault[]) {
    const persFaults: {[uid: string]: Fault; } = {};

    if (!allFaults) {
      allFaults = await this.getAll(fault.project.MyDocmaUid, [ ...fault.uid]);
    }
    allFaults.forEach(f => persFaults[f.uid] = f);

    let syncStateUpdated = false;
    const _f = allFaults?.find(x => x.uid === fault.uid);
    if (_f?.syncState && _f.syncState.status != fault?.syncState?.status) {
      fault.syncState.status = _f.syncState.status;
      syncStateUpdated = true;
    }
    if (_f?.syncState && _f.syncState.changes != fault?.syncState?.changes) {
      fault.syncState.changes = _f.syncState.changes;
      syncStateUpdated = true;
    }
    if (_f?.syncState && _f.syncState.error != fault?.syncState?.error) {
      fault.syncState.error = _f.syncState.error;
      syncStateUpdated = true;
    }
    if (_f?.syncState && _f.syncState.date != fault?.syncState?.date) {
      fault.syncState.date = _f.syncState.date;
      syncStateUpdated = true;
    }

    return syncStateUpdated;
  }

  async getFaultCount(mydocmaUid: string): Promise<FaultCount> {
    const dbService = await this.mmDbService.getProjectDb(mydocmaUid);
    const db = await dbService.db;
    const count = await db.count(MmProjectDbCollections.faults as never);
    return {AllCount: count};
  }
}
